import { useContext, useState } from "react";
import { Box, Grid, Paper, Typography, LinearProgress, Skeleton, Tabs, Tab, Stack } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import UserContext from "Context/UserProvider";
import { hasSharedInfrastructure } from "utils/categoryUtils";
import { isPlanProduct, isShareProduct } from "utils/productUtils";
import { isAccountRole } from "utils/roleUtils";
import { formatCurrency, formatNumber } from "utils/formatUtils";
import AccountInformation from "./account/AccountInformation";
import ChargingNetwork from "./account/ChargingNetwork";
import GettingStarted from "./account/GettingStarted";
import RecentFleets from "./account/RecentFleets";
import Scenarios from "./account/Scenarios";
import Bookings from "./business/Bookings";
import ChargingSessions from "./business/ChargingSessions";
import PartnerChargingNetwork from "./business/PartnerChargingNetwork";
import ChargingSummaryBarChart from "./business/savings/ChargingSummaryBarChart";
import ChargingSummaryPieChart from "./business/savings/ChargingSummaryPieChart";
import Billing from "./common/Billing";
import News from "./common/News";
import UserManagement from "./common/UserManagement";
import DashboardCard from "./common/DashboardCard";
import DashboardCardRow from "./common/DashboardCardRow";

type AccountDashboardProps = {
    dashboardInfo: any;
};

function LoadingSkeleton() {
    return (
        <Box sx={{ mb: 2 }}>
            <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
        </Box>
    );
}

function AccountDashboard({ dashboardInfo }: AccountDashboardProps) {
    const { userInfo, selectedProduct } = useContext(UserContext);
    const isAccountOnly = isAccountRole(userInfo.userType);
    const pcn = userInfo.privateChargingNetworkSubscription;
    const [activeTab, setActiveTab] = useState(0);

    const inboundTitle = "Recent Inbound Charging Sessions";
    const outboundTitle = "Recent Outbound Charging Sessions";

    if (!isAccountOnly) {
        return null;
    }

    if (!dashboardInfo) {
        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                        }}
                    >
                        <Typography variant="h5" gutterBottom>
                            Account Setup Progress
                        </Typography>
                        <Box sx={{ maxWidth: 600 }}>
                            <LoadingSkeleton />
                            <LoadingSkeleton />
                            <LoadingSkeleton />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        );
    }

    const progress = dashboardInfo?.onboardingProgress || {};

    const steps = [
        {
            label: 'Profile Setup',
            value: progress.profileSetup || 0,
            description: 'Complete your account profile information'
        },
        {
            label: 'Fleet Configuration',
            value: progress.fleetSetup || 0,
            description: 'Set up your vehicle fleet details'
        },
        {
            label: 'Payment Setup',
            value: progress.paymentSetup || 0,
            description: 'Configure your payment methods'
        }
    ];

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    if (selectedProduct !== null && isPlanProduct(selectedProduct)) {
        return (
            <Grid container spacing={3}
                sx={{
                    m: 0,
                    mt: -3,
                    ml: -3,
                    width: 'calc(100%)'
                }}
            >
                <Grid item xs={12} md={6} xl={4}>
                    <AccountInformation dashboardInfo={dashboardInfo} accentColor="info" />
                </Grid>
                <Grid item xs={12} md={6} xl={4}>
                    <RecentFleets dashboardInfo={dashboardInfo} accentColor="info" />
                </Grid>
                <Grid item xs={12} md={6} xl={4}>
                    <Scenarios dashboardInfo={dashboardInfo} accentColor="info" />
                </Grid>
                <Grid item xs={12} md={6} xl={4}>
                    <UserManagement dashboardInfo={dashboardInfo} accentColor="info" />
                </Grid>
                <Grid item xs={12} md={6} xl={4}>
                    <ChargingNetwork pcn={pcn} dashboardInfo={dashboardInfo} accentColor="info" />
                </Grid>
                <Grid item xs={12} md={6} xl={4}>
                    <News accentColor="info" />
                </Grid>
                <Grid item xs={12} md={6} xl={4}>
                    <Billing accentColor="info" />
                </Grid>
            </Grid>
        );
    }

    if (selectedProduct !== null && isShareProduct(selectedProduct)) {
        console.log("AD + " + JSON.stringify(dashboardInfo.inboundChargingSessions));

        return (
            <Box sx={{ width: '100%' }}>
                {/* Tab Navigation */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange}
                        sx={{
                            '& .MuiTab-root': {
                                minHeight: 64,
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                            },
                            '& .Mui-selected': {
                                color: 'primary.main',
                            }
                        }}
                    >
                        <Tab 
                            icon={<RocketLaunchIcon />} 
                            label="Onboarding Progress" 
                            iconPosition="start"
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                minHeight: 64 
                            }}
                        />
                        <Tab 
                            icon={<DashboardIcon />} 
                            label="Dashboard Overview" 
                            iconPosition="start"
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                minHeight: 64 
                            }}
                        />
                    </Tabs>
                </Box>

                {/* Tab Content */}
                {activeTab === 0 && (
                    <Grid container spacing={3} sx={{ m: 0, mt: -3, ml: -3, width: 'calc(100%)' }}>
                        <Grid item xs={12}>
                            <GettingStarted onboardingProgress={dashboardInfo.onboardingProgress} />
                        </Grid>
                    </Grid>
                )}

                {activeTab === 1 && (
                    <Grid
                        container
                        spacing={3}
                        sx={{
                            m: 0,
                            mt: -3,
                            ml: -3,
                            width: 'calc(100%)'
                        }}
                    >
                        {/* Account Overview Panel */}
                        <Grid item xs={12} md={4}>
                            <DashboardCard 
                                title="Account Overview" 
                                icon={<AssessmentIcon />}
                                noPadding
                                accentColor="info"
                            >
                                <Box p={2}>
                                    <Stack spacing={2}>
                                        <DashboardCardRow
                                            title="Total Fleets"
                                            value={formatNumber(dashboardInfo?.totalFleets)}
                                        />
                                        <DashboardCardRow
                                            title="Total Vehicles"
                                            value={formatNumber(dashboardInfo?.totalVehicles)}
                                        />
                                        <DashboardCardRow
                                            title="Last Accessed"
                                            value={dashboardInfo?.lastAccessedDate || 'N/A'}
                                        />
                                    </Stack>
                                </Box>
                            </DashboardCard>
                        </Grid>
                        
                        {/* Charging Network Panel */}
                        <Grid item xs={12} md={4}>
                            <DashboardCard 
                                title="Charging Network" 
                                icon={<TimelineIcon />}
                                noPadding
                                accentColor="warning"
                            >
                                <Box p={2}>
                                    <Stack spacing={2}>
                                        <DashboardCardRow
                                            title="Partners in Network"
                                            value={formatNumber(dashboardInfo?.businessesInPCN)}
                                        />
                                        <DashboardCardRow
                                            title="Charge Points"
                                            value={formatNumber(dashboardInfo?.countChargePointsInNetwork)}
                                        />
                                        <DashboardCardRow
                                            title="Business Code"
                                            value={dashboardInfo?.businessCode || 'N/A'}
                                        />
                                    </Stack>
                                </Box>
                            </DashboardCard>
                        </Grid>
                        
                        {/* Cost Analysis Panel */}
                        <Grid item xs={12} md={4}>
                            <DashboardCard 
                                title="Cost Analysis" 
                                icon={<TrendingUpIcon />}
                                noPadding
                                accentColor="primary"
                            >
                                <Box p={2}>
                                    <Stack spacing={2}>
                                        <DashboardCardRow
                                            title="Account Balance"
                                            value={formatCurrency(dashboardInfo?.currentBalance)}
                                        />
                                        <DashboardCardRow
                                            title="Partner Charging Cost"
                                            value={formatCurrency(dashboardInfo?.currentMonth?.partnerChargingCost)}
                                        />
                                        <DashboardCardRow
                                            title="Public Charging Cost"
                                            value={formatCurrency(dashboardInfo?.currentMonth?.publicChargingCost)}
                                        />
                                    </Stack>
                                </Box>
                            </DashboardCard>
                        </Grid>

                        <Grid item xs={12} md={6} xl={4}>
                            <UserManagement dashboardInfo={dashboardInfo} accentColor="info" />
                        </Grid>
                        <Grid item xs={12} md={6} xl={4}>
                            <PartnerChargingNetwork dashboardInfo={dashboardInfo} accentColor="info" />
                        </Grid>
                        <Grid item xs={12} md={6} xl={4}>
                            <Bookings dashboardInfo={dashboardInfo} accentColor="info" />
                        </Grid>
                        {hasSharedInfrastructure(userInfo.businessCategory) && (
                            <Grid item xs={12}>
                                <ChargingSessions
                                    title="Recent Inbound Charging Sessions"
                                    chargingSessions={dashboardInfo.inboundChargingSessions}
                                    incomeCostTitle="Income"
                                    accentColor="info"
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <ChargingSessions
                                title="Recent Outbound Charging Sessions"
                                chargingSessions={dashboardInfo.outboundChargingSessions}
                                incomeCostTitle="Cost"
                                accentColor="info"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ChargingSummaryBarChart 
                                data={[
                                    { name: "Partner Charging Cost", price: Math.round(dashboardInfo.currentMonth?.partnerChargingCost) },
                                    { name: "Est. Public Charging Cost", price: Math.round(dashboardInfo.currentMonth?.publicChargingCost) },
                                ]}
                                dashboardInfo={dashboardInfo}
                                accentColor="info"
                            />
                        </Grid>
                        {hasSharedInfrastructure(userInfo.businessCategory) && (
                            <Grid item xs={12} md={6}>
                                <ChargingSummaryPieChart
                                    total={dashboardInfo.countBusinessesServedByYourChargePoints + "/" + dashboardInfo.businessesInPCN}
                                    unit={"Businesses"}
                                    fleetScenarioUuid={""}
                                    fleetScenarioNumber={""}
                                    title={"Percentage of Businesses using your charge points"}
                                    height={400}
                                    width={400}
                                    data={dashboardInfo.inboundBusinessUsage}
                                    accentColor="info"
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} md={6}>
                            <ChargingSummaryPieChart
                                total={dashboardInfo.countChargePointsUsedByYourBusiness + "/" + dashboardInfo.countChargePointsInNetwork}
                                unit={"Charge Points"}
                                fleetScenarioUuid={""}
                                fleetScenarioNumber={""}
                                title={"Percentage of Network Charge Points used"}
                                height={400}
                                width={400}
                                data={dashboardInfo.outboundChargePointUsage}
                                accentColor="info"
                            />
                        </Grid>
                        <Grid item xs={12} md={6} xl={4}>
                            <News accentColor="info" />
                        </Grid>
                        <Grid item xs={12} md={6} xl={4}>
                            <Billing accentColor="info" />
                        </Grid>
                    </Grid>
                )}
            </Box>
        );
    }

    return null;
}

export default AccountDashboard;
